import type { TUuid } from '../types/common';
import { ERoleIdentifier, EUserPosition, type TUserWithRole } from '../types/user';

// Display order for position pickers; not the same as EUserPosition's declaration order.
export const userPositions: ReadonlyArray<EUserPosition> = [
  EUserPosition.Stammpersonal,
  EUserPosition.Facharzt,
  EUserPosition.Oberarzt,
  EUserPosition.Chefarzt,
];

/** The frontend's `userPositionDefault` (null) as a filter value, which cannot be null. */
export const USER_POSITION_DEFAULT_FILTER = 'AIW';

export const administrativeAccessRoles: ReadonlyArray<ERoleIdentifier> = [
  ERoleIdentifier.ADMINISTRATIVE_STAFF,
  ERoleIdentifier.READ_ONLY,
];

export const appAccessRoles: ReadonlyArray<ERoleIdentifier> = [
  ...administrativeAccessRoles,
  ERoleIdentifier.ROTATING_STAFF,
];

/** A missing user or role never matches. */
export const hasUserRole = (
  user: TUserWithRole | null | undefined,
  roles: ERoleIdentifier | ReadonlyArray<ERoleIdentifier>,
): boolean => {
  const identifier = user?.role?.identifier;
  const allowed: ReadonlyArray<string> = typeof roles === 'string' ? [roles] : roles;
  return !!identifier && allowed.includes(identifier);
};

type TUserWithRotationFlag = TUserWithRole
  & Readonly<{ include_in_rotation?: boolean | null }>;

/* The flag is no condition for ROTATING_STAFF: their lists show people out of the rotation too. */
export const isRotatingUser = (
  user: TUserWithRotationFlag | null | undefined,
): boolean => (
  hasUserRole(user, ERoleIdentifier.ROTATING_STAFF)
  || (hasUserRole(user, ERoleIdentifier.ADMINISTRATIVE_STAFF) && user?.include_in_rotation === true)
);

export const isAdministrativeStaff = (user: TUserWithRole | null | undefined): boolean => (
  hasUserRole(user, ERoleIdentifier.ADMINISTRATIVE_STAFF)
);

type TRotatingUserClause =
  | Readonly<{ role: Readonly<{ identifier: Readonly<{ _eq: ERoleIdentifier }> }> }>
  | Readonly<{ include_in_rotation: Readonly<{ _eq: true }> }>;

type TRotatingUserFilter<TClause> = {
  _or: Array<TClause | { _and: TClause[] }>
};

export const buildRotatingUserFilter = <TClause>(
  wrapField: (clause: TRotatingUserClause) => TClause,
): TRotatingUserFilter<TClause> => ({
  _or: [
    wrapField({ role: { identifier: { _eq: ERoleIdentifier.ROTATING_STAFF } } }),
    {
      _and: [
        wrapField({ role: { identifier: { _eq: ERoleIdentifier.ADMINISTRATIVE_STAFF } } }),
        wrapField({ include_in_rotation: { _eq: true } }),
      ],
    },
  ],
});

export const rotatingUserFilter = buildRotatingUserFilter((clause) => clause);

export type TUserDeletionCaller = TUserWithRole & Readonly<{
  mainFacilityId?: TUuid | null,
  isMainFacilityAdmin?: boolean | null,
}>;

export type TUserDeletionTarget = TUserWithRole & Readonly<{
  mainFacilityId?: TUuid | null,
  facilityCount: number,
}>;

/**
 * A user without any facility can only be deleted by a global planner of the same Verbund, and only
 * an app user (never an admin); a user with facilities is governed by the facility permissions.
 */
export const mayDeleteUserWithoutFacility = (
  caller: TUserDeletionCaller,
  target: TUserDeletionTarget,
): boolean => target.facilityCount === 0
  && hasUserRole(target, appAccessRoles)
  && hasUserRole(caller, ERoleIdentifier.ADMINISTRATIVE_STAFF)
  && caller.isMainFacilityAdmin === true
  && !!caller.mainFacilityId
  && caller.mainFacilityId === target.mainFacilityId;
